import { createContext, useContext, useEffect, useMemo } from 'react'
import { useCandleStore } from '../../hooks/useCandleStore.js'
import { subscribeIbkrChartStream } from '../../lib/marketData/ibkrChartAdapter.js'

const Context = createContext(null)
const EVENTS = ['onFormingCandle', 'onCompletedCandle', 'onPrice', 'onState', 'onError']

// Scoped to one authenticated workspace. Never retain tickets/data after its
// consumers leave, and never cancel another panel's still-owned connection.
export function createChartStreamFanout(start = subscribeIbkrChartStream) {
  const streams = new Map()
  return {
    subscribe(options) {
      const key = options.streamUrl
      let entry = streams.get(key)
      if (!entry) {
        entry = { listeners: new Set(), state: null, recent: new Map(), stop: null }
        streams.set(key, entry)
        entry.listeners.add(options)
        try {
          entry.stop = start({ ...options, ...Object.fromEntries(EVENTS.map(event => [event, (...args) => {
            if (event === 'onState') entry.state = args[0]
            if (['onFormingCandle', 'onCompletedCandle', 'onPrice'].includes(event)) {
              const id = `${event}:${args[0]?.time || ''}`
              entry.recent.delete(id)
              entry.recent.set(id, [event, args])
              while (entry.recent.size > 32) entry.recent.delete(entry.recent.keys().next().value)
            }
            for (const listener of [...entry.listeners]) listener[event]?.(...args)
          }])) })
        } catch (error) { streams.delete(key); throw error }
      } else {
        entry.listeners.add(options)
        if (entry.state) options.onState?.(entry.state)
        // A newly revealed panel starts from the shared snapshot, then catches
        // up with bounded live corrections already received by its sibling.
        for (const [event, args] of entry.recent.values()) options[event]?.(...args)
      }
      return () => {
        if (!entry.listeners.delete(options)) return
        if (!entry.listeners.size) { entry.stop?.(); streams.delete(key) }
      }
    },
    dispose() {
      for (const entry of streams.values()) { entry.stop?.(); entry.listeners.clear() }
      streams.clear()
    },
  }
}

export function WorkspaceDailySource({ chartProps, enabled, children }) {
  const { symbol, mode, candleAuthority, assetType = 'equity', timezone = 'America/Chicago' } = chartProps
  const active = enabled && mode === 'live' && candleAuthority === 'ibkr-runtime'
    && assetType === 'equity' && symbol !== 'MES1!'
  const source = useCandleStore({ symbol, timeframe: 'daily', marketSession: 'regular', timezone,
    candleAuthority, enabled: Boolean(active) })
  const pool = useMemo(() => createChartStreamFanout(), [])
  useEffect(() => () => pool.dispose(), [pool])
  const value = useMemo(() => active ? { ...source, symbol, timezone, subscribe: pool.subscribe } : null,
    [active, source.candles, source.status, source.error, source.retry, source.requestKey, source.candlesKey,
      source.streamUrl, source.reconcileStream, source.loadMoreBefore, symbol, timezone, pool])
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useWorkspaceDailySource({ symbol, timeframe, marketSession, timezone, candleAuthority, enabled = true }) {
  const value = useContext(Context)
  return enabled && candleAuthority === 'ibkr-runtime' && timeframe === 'daily'
    && marketSession === 'regular' && value?.symbol === symbol && value.timezone === timezone ? value : null
}
